'use client'

import { useEffect, useState } from 'react';

export interface User {
    id?: string;
    name?: string;
    email?: string;
}

export interface FaceBookLoginState {
    isSdkLoaded: boolean;
    isProcessing: boolean;
    isLoggedIn: boolean;
    token?: string;
    currentUser?: User;
    loaded: boolean;
}

type FacebookFields = 'name' | 'email' | 'gender' | 'id';

export interface FaceBookLoginProps {
    appId: string;
    language: string;
    version: string;
    fields: FacebookFields[];
    onFailure?: (response: any) => void;
}

const getWindow = (): any => {
    return window as any;
};

const getUserInfo = (
    props: FaceBookLoginProps,
    state: FaceBookLoginState,
    setState: (state: FaceBookLoginState) => void
): void => {
    getWindow().FB.api('/me', { locale: props.language, fields: props.fields.join(',') }, (response: any) => {
        const currentUser: User = response;
        setState({
            ...state,
            isLoggedIn: true,
            currentUser,
            isProcessing: false,
            loaded: true,
        });
    });
};

const checkLoginCallback = (
    response: any,
    props: FaceBookLoginProps,
    state: FaceBookLoginState,
    setState: (state: FaceBookLoginState) => void
): void => {
    if (response.status === 'connected') {
        console.log(`${new Date().toISOString()} [Next.js] [Facebook] facebook auto login response`, response)
        state.token = response.authResponse.accessToken;
        // setState({...state, isLoggedIn: true, isProcessing: false, loaded: true, token: response.authResponse.accessToken})
        getUserInfo(props, state, setState);
    } else {
        setState({
            ...state,
            token: undefined,
            isLoggedIn: false,
            isProcessing: false,
        });
    }
};

const setFacekbookAsyncInit = (
    props: FaceBookLoginProps,
    state: FaceBookLoginState,
    setState: (state: FaceBookLoginState) => void
): void => {
    getWindow().fbAsyncInit = () => {
        getWindow().FB.init({
            version: `v${props.version}`,
            appId: `${props.appId}`,
            xfbml: false,
            cookie: false,
        });

        setState({
            ...state,
            isSdkLoaded: true,
        });

        // TODO UNCOMMANT
        // getWindow().FB.getLoginStatus((response: any) => checkLoginCallback(response, props, state, setState));
    };
};

const loadSdkAsynchronously = (props: FaceBookLoginProps, state: FaceBookLoginState): void => {
    ((doc: Document, script: string, sdkId: string) => {
        const newScriptElement = doc.createElement(script) as HTMLScriptElement;

        newScriptElement.id = sdkId;
        newScriptElement.src = `https://connect.facebook.net/${props.language}/sdk.js`;
        doc.head.appendChild(newScriptElement);

        let fbRoot = doc.getElementById('fb-root');
        if (!fbRoot) {
            fbRoot = doc.createElement('div');
            fbRoot.id = 'fb-root';
            doc.body.appendChild(fbRoot);
        }
    })(document, 'script', 'facebook-jssdk');
};

const loginCallback = (
    response: any,
    props: FaceBookLoginProps,
    state: FaceBookLoginState,
    setState: (state: FaceBookLoginState) => void
): void => {
    console.log(`${new Date().toISOString()} [Next.js] [Facebook] response`, response)
    if (response.authResponse) {
        // setState({...state,  isLoggedIn: true, isProcessing: false, loaded: true, token: response.authResponse.accessToken})
        state.token = response.authResponse.accessToken;
        getUserInfo(props, state, setState);
    } else {
        if (props.onFailure) {
            props.onFailure(response);
            setState({ ...state, isLoggedIn: false, token: undefined, isProcessing: false });
        }
    }
};

const logoutCallback = (
    response: any,
    props: FaceBookLoginProps,
    state: FaceBookLoginState,
    setState: (state: FaceBookLoginState) => void
): void => {
    console.log(`${new Date().toISOString()} [Next.js] [Facebook] facebook logout response`, response)
    // if (response.authResponse) {
    setState({
        ...state,
        token: undefined,
        isLoggedIn: false,
        isProcessing: false,
    });
    // } else {
    //     if (props.onFailure) {
    //         props.onFailure(response);
    //     }
    // }
};

export const useFacebook = (props: FaceBookLoginProps): [FaceBookLoginState, () => void, () => void] => {
    const [state, setState] = useState<FaceBookLoginState>({
        isSdkLoaded: false,
        isProcessing: false,
        isLoggedIn: false,
        loaded: false,
    });

    const login = (): void => {
        setState({ ...state, isProcessing: true });
        getWindow().FB.login((response: any) => loginCallback(response, props, state, setState));
    };

    const logout = (): void => {
        setState({ ...state, isProcessing: true });
        getWindow().FB.logout((response: any) => logoutCallback(response, props, state, setState));
    };

    useEffect(() => {
        setState({
            ...state,
            isProcessing: true,
        });

        setFacekbookAsyncInit(props, state, setState);
        loadSdkAsynchronously(props, state);
    }, [props.appId, props.fields ? props.fields.join(',') : '', props.language, props.version]);

    return [state, login, logout];
};
